import React from 'react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Button from '../utils/Button';
import { US_STATES } from '../utils/Constant';

// Helper function to convert state code to state name
const getStateName = (stateCode: string): string => {
	const state = US_STATES.find(s => s.code === stateCode);
	return state?.name || '';
};

interface ElectionDatesProps {
	isElectionLoading: boolean;
	electionError: any;
	electionData: any;
	engagementData: any;
	onShowReturnFlow: () => void;
	onNextStep: () => void;
	onSetCurrentFlow: React.Dispatch<React.SetStateAction<'polling' | 'reminders' | 'dates' | null>>;
	onSetCurrentStep: (step: number) => void;
}

const ElectionDates: React.FC<ElectionDatesProps> = ({
	isElectionLoading,
	electionError,
	electionData,
	engagementData,
	onShowReturnFlow,
	onNextStep,
	onSetCurrentFlow,
	onSetCurrentStep,
}) => {
	return (
		<div className="bg-white rounded-xl shadow-lg p-6">
			<h2 className="text-2xl text-gray-900 font-semibold mb-2">Important Voting Dates</h2>
			<p className="text-gray-600 text-sm mb-6">
				Stay informed about key election dates and deadlines.
			</p>

			{isElectionLoading ? (
				<div className="flex items-center justify-center py-8">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
					<span className="ml-3 text-gray-600">Loading election information...</span>
				</div>
			) : electionError != null || electionData?.data?.elections.length === 0 ? (
				<div className="text-center py-8">
					<ErrorOutlineIcon className="text-gray-400 text-5xl mb-4 mx-auto" />
					<h3 className="text-lg font-semibold text-gray-900 mb-2">
						No Elections Available
					</h3>
					<p className="text-gray-600 mb-6">
						There are no elections for {getStateName(engagementData.state)} at the moment.
					</p>
					<button
						onClick={() => {
							onSetCurrentFlow('reminders');
							onSetCurrentStep(3);
						}}
						className="text-blue-600 hover:text-blue-800 font-medium underline"
					>
						Kindly opt in for election reminders
					</button>

					<div className="flex justify-between mt-8">
						<Button
							label="Back"
							variant="secondary"
							onClick={onShowReturnFlow}
						/>
						<Button
							label="Next Step →"
							variant="primary"
							onClick={onNextStep}
						/>
					</div>
				</div>
			) : (
				<>
					<div className="space-y-4 mb-6">
						{electionData?.data?.elections && electionData?.data?.elections?.map((election: any, index: number) => (
							<div key={index} className="border border-gray-200 rounded-lg p-4 mb-4">
								<h3 className="font-semibold text-gray-900 mb-3">{election.name}</h3>
								<div className="space-y-3">
									{election.registrationDeadline && (
										<div className="border-l-4 border-blue-500 pl-4 py-2">
											<h4 className="font-medium text-gray-900">Voter Registration Deadline</h4>
											<p className="text-gray-600">{election.registrationDeadline}</p>
										</div>
									)}
									{election.earlyVotingStartDate && (
										<div className="border-l-4 border-purple-500 pl-4 py-2">
											<h4 className="font-medium text-gray-900">Early Voting Begins</h4>
											<p className="text-gray-600">{election.earlyVotingStartDate}</p>
										</div>
									)}
									{election.electionDay && (
										<div className="border-l-4 border-green-500 pl-4 py-2">
											<h4 className="font-medium text-gray-900">Election Day</h4>
											<p className="text-gray-600">{election.electionDay}</p>
										</div>
									)}
								</div>
							</div>
						))}
					</div>

					<div className="flex justify-between">
						<Button
							label="Back"
							variant="secondary"
							onClick={onShowReturnFlow}
						/>
						<Button
							label="Next Step →"
							variant="primary"
							onClick={onNextStep}
						/>
					</div>
				</>
			)}
		</div>
	);
};

export default ElectionDates;
